/** Ordered block/list reference editor for one notification.
 *
 * Each row targets EITHER a Notification_Block or a Block_List (tenant +
 * system rows), with an optional alias overriding the name it appears under
 * in the `block.*` namespace. Rows drag-reorder (dnd-kit) with up/down
 * buttons as the keyboard/fallback path. New rows get negative ids — the
 * write mapper (`refsToWrite`) nulls them so the funnel creates them.
 */
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';

import * as blockListsApi from '@/api/blockLists';
import * as blocksApi from '@/api/notificationBlocks';
import type { BlockRefItem } from '@/api/notifications';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';

import { blockActions, blockListActions } from '../notificationLinks';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';

interface TargetOption {
  id: number;
  codename: string;
  name: string;
}

type RowKind = 'block' | 'list';

function rowKind(row: BlockRefItem, override: RowKind | undefined): RowKind {
  if (row.block != null) return 'block';
  if (row.block_list != null) return 'list';
  return override ?? 'block';
}

function resequence(rows: BlockRefItem[]): BlockRefItem[] {
  return rows.map((r, idx) => ({ ...r, order: idx }));
}

export interface BlockRefsEditorProps {
  value: BlockRefItem[];
  onChange: (refs: BlockRefItem[]) => void;
  /** Owner scope for the target lookups ('system' = no tenant). */
  tenantId: number | 'system';
  /** Row errors keyed by row index; '*' = general. */
  errors?: Record<string, string> | undefined;
  disabled?: boolean | undefined;
}

export function BlockRefsEditor({ value, onChange, tenantId, errors, disabled }: BlockRefsEditorProps) {
  // Type choice for rows whose target was cleared (both FKs null).
  const [kindOverrides, setKindOverrides] = useState<Record<number, RowKind>>({});
  const nextTempIdRef = useRef(-Date.now());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchBlocks = useCallback(
    (q: string): Promise<TargetOption[]> =>
      blocksApi
        .list({ search: q, tour_operator_id: tenantId, pageSize: 20 })
        .then((r) => r.results.map((b) => ({ id: b.id, codename: b.codename, name: b.name }))),
    [tenantId],
  );
  const fetchLists = useCallback(
    (q: string): Promise<TargetOption[]> =>
      blockListsApi
        .list({ search: q, tour_operator_id: tenantId, pageSize: 20 })
        .then((r) => r.results.map((l) => ({ id: l.id, codename: l.codename, name: l.name }))),
    [tenantId],
  );

  const updateRow = (idx: number, next: BlockRefItem) => {
    onChange(value.map((r, i) => (i === idx ? next : r)));
  };

  const move = (idx: number, delta: number) => {
    const target = idx + delta;
    if (target < 0 || target >= value.length) return;
    onChange(resequence(arrayMove(value, idx, target)));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = value.findIndex((r) => r.id === Number(active.id));
    const newIndex = value.findIndex((r) => r.id === Number(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(resequence(arrayMove(value, oldIndex, newIndex)));
  };

  const addRow = () => {
    nextTempIdRef.current -= 1;
    onChange([
      ...value,
      {
        id: nextTempIdRef.current,
        order: value.length,
        block: null,
        block_codename: null,
        block_list: null,
        block_list_codename: null,
        alias: null,
      },
    ]);
  };

  const generalError = errors?.['*'];

  return (
    <Stack spacing={1}>
      {generalError && <Alert severity="error">{generalError}</Alert>}
      {value.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No block references — standard layouts will only see the default blocks.
        </Typography>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          {value.map((row, idx) => (
            <SortableRefRow
              key={row.id}
              row={row}
              kind={rowKind(row, kindOverrides[row.id])}
              disabled={Boolean(disabled)}
              error={errors?.[String(idx)]}
              isFirst={idx === 0}
              isLast={idx === value.length - 1}
              fetchBlocks={fetchBlocks}
              fetchLists={fetchLists}
              onKindChange={(kind) => {
                setKindOverrides((prev) => ({ ...prev, [row.id]: kind }));
                updateRow(idx, {
                  ...row,
                  block: null,
                  block_codename: null,
                  block_list: null,
                  block_list_codename: null,
                });
              }}
              onTargetChange={(kind, opt) =>
                updateRow(
                  idx,
                  kind === 'block'
                    ? {
                        ...row,
                        block: opt?.id ?? null,
                        block_codename: opt?.codename ?? null,
                        block_list: null,
                        block_list_codename: null,
                      }
                    : {
                        ...row,
                        block: null,
                        block_codename: null,
                        block_list: opt?.id ?? null,
                        block_list_codename: opt?.codename ?? null,
                      },
                )
              }
              onAliasChange={(alias) => updateRow(idx, { ...row, alias: alias || null })}
              onMoveUp={() => move(idx, -1)}
              onMoveDown={() => move(idx, 1)}
              onRemove={() => onChange(resequence(value.filter((_, i) => i !== idx)))}
            />
          ))}
        </SortableContext>
      </DndContext>
      {!disabled && (
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={addRow}>
            Add block reference
          </Button>
        </Box>
      )}
    </Stack>
  );
}

interface RowProps {
  row: BlockRefItem;
  kind: RowKind;
  disabled: boolean;
  error?: string | undefined;
  isFirst: boolean;
  isLast: boolean;
  fetchBlocks: (q: string) => Promise<TargetOption[]>;
  fetchLists: (q: string) => Promise<TargetOption[]>;
  onKindChange: (kind: RowKind) => void;
  onTargetChange: (kind: RowKind, opt: TargetOption | null) => void;
  onAliasChange: (alias: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function SortableRefRow({
  row,
  kind,
  disabled,
  error,
  isFirst,
  isLast,
  fetchBlocks,
  fetchLists,
  onKindChange,
  onTargetChange,
  onAliasChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled,
  });

  const targetId = kind === 'block' ? row.block : row.block_list;
  const targetCodename = kind === 'block' ? row.block_codename : row.block_list_codename;
  const selected: TargetOption | null =
    targetId != null
      ? { id: targetId, codename: targetCodename ?? `#${targetId}`, name: '' }
      : null;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton
          size="small"
          aria-label="Drag to reorder"
          disabled={disabled}
          sx={{ cursor: 'grab', touchAction: 'none' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" aria-label="Move up" disabled={disabled || isFirst} onClick={onMoveUp}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" aria-label="Move down" disabled={disabled || isLast} onClick={onMoveDown}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={kind}
          disabled={disabled}
          onChange={(_, v: RowKind | null) => v && v !== kind && onKindChange(v)}
        >
          <ToggleButton value="block">Block</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          {/* key remounts the autocomplete when the row switches kind so its
              option cache never mixes blocks and lists */}
          <RemoteAutocomplete<TargetOption>
            key={kind}
            size="small"
            label={kind === 'block' ? 'Block' : 'Block list'}
            queryFn={kind === 'block' ? fetchBlocks : fetchLists}
            getOptionLabel={(o) => (o.name ? `${o.codename} — ${o.name}` : o.codename)}
            getOptionKey={(o) => o.id}
            value={selected}
            onChange={(v) => !disabled && onTargetChange(kind, v as TargetOption | null)}
            fetchOnEmpty
          />
          {targetId != null && (
            <PickerLinks
              items={[
                {
                  key: targetId,
                  label: targetCodename ?? `#${targetId}`,
                  actions:
                    kind === 'block'
                      ? blockActions({ id: targetId, codename: targetCodename })
                      : blockListActions({ id: targetId, codename: targetCodename }),
                },
              ]}
            />
          )}
        </Box>
        <TextField
          size="small"
          label="Alias"
          placeholder="optional"
          value={row.alias ?? ''}
          disabled={disabled}
          onChange={(e) => onAliasChange(e.target.value)}
          sx={{ width: 180 }}
        />
        <Tooltip title={kind === 'block' ? 'Open block definition in a new tab' : 'Open block list in a new tab'}>
          <span>
            <IconButton
              size="small"
              aria-label={kind === 'block' ? 'Open block definition' : 'Open block list'}
              disabled={targetId == null}
              component="a"
              href={
                targetId == null
                  ? undefined
                  : kind === 'block'
                    ? `/notifications/blocks?edit=${targetId}`
                    : `/notifications/block-lists?edit=${targetId}`
              }
              target="_blank"
              rel="noopener"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" aria-label="Remove reference" disabled={disabled} onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      {error && <FormHelperText error sx={{ ml: 5 }}>{error}</FormHelperText>}
    </Box>
  );
}
